import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, MapPin, Shield, DollarSign, Clock, Star, ChevronRight, Car, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Navbar } from '@/components/Navbar';
import axios from 'axios';
import { API } from '@/App';

const LandingPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [user, setUser] = useState(null);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    // Check if user is logged in (try localStorage token first, then cookie)
    const checkAuth = async () => {
      try {
        const storedToken = localStorage.getItem('session_token');
        const headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};
        
        const res = await axios.get(`${API}/auth/me`, { 
          withCredentials: true,
          headers 
        });
        setUser(res.data);
      } catch {
        // Clear invalid token
        localStorage.removeItem('session_token');
        setUser(null);
      } finally {
        setCheckingAuth(false);
      }
    };
    
    checkAuth();

    // Fetch featured listings
    axios.get(`${API}/listings?limit=6`)
      .then(res => setFeaturedListings(res.data))
      .catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  const handleLogin = () => {
    navigate('/auth?from=landing');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar transparent user={user} fixed={false} />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div className="container-main grid lg:grid-cols-2 gap-12 items-center py-20">
          {/* Left - Content */}
          <div className="space-y-8 animate-fadeIn">
            <div className="space-y-4">
              <span className="inline-block px-4 py-2 bg-green-50 text-[#5ebc67] rounded-full text-sm font-medium">
                Sydney's #1 Parking Marketplace
              </span>
              <h1 className="text-4xl md:text-6xl font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope' }}>
                Find parking spots.
                <br />
                <span className="text-[#5ebc67]">Share your driveway.</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-lg leading-relaxed">
                Connect with homeowners who have spare parking. Save money, time, and hassle finding the perfect spot near your destination.
              </p>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="relative max-w-xl">
              <div className="flex items-center bg-white rounded-full shadow-lg border border-slate-100 p-2">
                <div className="flex items-center gap-2 px-4 flex-1">
                  <MapPin className="w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Where do you need parking?"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="border-0 shadow-none focus-visible:ring-0 text-lg placeholder:text-slate-400"
                    data-testid="hero-search-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="bg-[#5ebc67] hover:bg-[#4da855] text-white rounded-full px-8 py-6"
                  data-testid="hero-search-btn"
                >
                  <Search className="w-5 h-5" />
                </Button>
              </div>
            </form>

            {/* Quick Stats */}
            <div className="flex items-center gap-8 pt-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-900">Sydney</p>
                <p className="text-sm text-slate-500">Launch city</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-900">24/7</p>
                <p className="text-sm text-slate-500">Access available</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-900">Secure</p>
                <p className="text-sm text-slate-500">Verified hosts</p>
              </div>
            </div>
          </div>

          {/* Right - Image */}
          <div className="relative hidden lg:block">
            <img
              src="https://images.unsplash.com/photo-1748798338095-bb0246f42dec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxzeWRuZXklMjBhdXN0cmFsaWElMjBza3lsaW5lJTIwc3Vubnl8ZW58MHx8fHwxNzY4NjIxMzI2fDA&ixlib=rb-4.1.0&q=85&w=800"
              alt="Sydney skyline"
              className="rounded-3xl shadow-2xl w-full h-[500px] object-cover"
            />
            
            {/* Floating Card */}
            <div className="absolute -bottom-6 -left-6 glass rounded-2xl p-4 shadow-xl animate-slideUp" style={{ animationDelay: '0.3s' }}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-[#5ebc67]" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Save up to 60%</p>
                  <p className="text-sm text-slate-500">vs commercial parking</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Two Cards */}
      <section className="section-padding bg-slate-50">
        <div className="container-main">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Driver CTA */}
            <div 
              className="group relative overflow-hidden rounded-3xl bg-white p-8 md:p-12 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border border-slate-100"
              onClick={() => navigate('/search')}
              data-testid="cta-find-parking"
            >
              <div className="relative z-10">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6">
                  <Car className="w-8 h-8 text-blue-600" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>
                  Find Parking
                </h2>
                <p className="text-slate-600 mb-6 max-w-sm">
                  Browse hundreds of available spots near your destination. Book instantly and pay securely.
                </p>
                <div className="flex items-center text-[#5ebc67] font-semibold group-hover:gap-3 transition-all">
                  <span>Search now</span>
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full -translate-y-1/2 translate-x-1/2 opacity-50"></div>
            </div>

            {/* Host CTA */}
            <div 
              className="group relative overflow-hidden rounded-3xl bg-slate-900 p-8 md:p-12 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
              onClick={handleLogin}
              data-testid="cta-list-driveway"
            >
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#5ebc67]/20 rounded-2xl flex items-center justify-center mb-6">
                  <Home className="w-8 h-8 text-[#5ebc67]" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
                  List Your Driveway
                </h2>
                <p className="text-slate-300 mb-6 max-w-sm">
                  Earn money from your unused parking space. Set your own prices and availability.
                </p>
                <div className="flex items-center text-[#5ebc67] font-semibold group-hover:gap-3 transition-all">
                  <span>Start earning</span>
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#5ebc67]/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>
              Why Choose SpotFinder?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              The smarter way to park in Sydney. Save time, money, and stress.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 stagger-children">
            <div className="card-feature">
              <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                <DollarSign className="w-7 h-7 text-[#5ebc67]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Save Money</h3>
              <p className="text-slate-600 leading-relaxed">
                Find parking spots at up to 60% less than commercial rates. No hidden fees or surge pricing.
              </p>
            </div>

            <div className="card-feature">
              <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <Clock className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Save Time</h3>
              <p className="text-slate-600 leading-relaxed">
                Book your spot in advance. No more driving around looking for parking.
              </p>
            </div>

            <div className="card-feature">
              <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Secure & Trusted</h3>
              <p className="text-slate-600 leading-relaxed">
                Verified hosts, secure payments, and 24/7 support. Park with confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-slate-50">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>
              How It Works
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Search', desc: 'Enter your destination and find nearby spots' },
              { step: '2', title: 'Compare', desc: 'View prices, ratings, and availability' },
              { step: '3', title: 'Book', desc: 'Reserve your spot instantly' },
              { step: '4', title: 'Park', desc: 'Follow directions and park stress-free' }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-[#5ebc67] rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>
              What Our Users Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: 'Sarah M.', role: 'Driver', text: 'Found a spot right next to my office. Saving $200/month compared to the parking garage!', rating: 5 },
              { name: 'James T.', role: 'Host', text: 'Making extra income from my empty driveway while I\'m at work. Easy money!', rating: 5 },
              { name: 'Lisa K.', role: 'Driver', text: 'The app is so easy to use. Booked a spot for the weekend in 2 minutes.', rating: 5 }
            ].map((review, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(review.rating)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 star-filled" fill="currentColor" />
                  ))}
                </div>
                <p className="text-slate-600 mb-4 leading-relaxed">"{review.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-full"></div>
                  <div>
                    <p className="font-semibold text-slate-900">{review.name}</p>
                    <p className="text-sm text-slate-500">{review.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-slate-900">
        <div className="container-main text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope' }}>
            Ready to find your perfect spot?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-xl mx-auto">
            Join thousands of Sydney drivers who save time and money with SpotFinder.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/search')}
              className="bg-[#5ebc67] hover:bg-[#4da855] text-white rounded-full px-8 py-6 text-lg font-semibold"
              data-testid="final-cta-search"
            >
              Find Parking Now
            </Button>
            <Button
              onClick={handleLogin}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-slate-900 rounded-full px-8 py-6 text-lg font-semibold"
              data-testid="final-cta-host"
            >
              List Your Driveway
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-950">
        <div className="container-main">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_spotfinder-96/artifacts/rmerhn11_spotfinder-high-resolution-logo%20%281%29%20%281%29.png" 
              alt="SpotFinder" 
              className="h-8 w-auto brightness-0 invert"
            />
            <div className="flex items-center gap-6">
              <Link to="/about" className="text-slate-400 hover:text-white text-sm transition-colors">
                About
              </Link>
              <Link to="/terms" className="text-slate-400 hover:text-white text-sm transition-colors">
                Terms of Service
              </Link>
              <Link to="/privacy" className="text-slate-400 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
            </div>
            <p className="text-slate-400 text-sm">
              © 2026 SpotFinder. Sydney's parking marketplace.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
